<template>
  <e-dialog ref="dialogRef" wide @open="handleDialogOpen" @close="handleDialogClose">
    <template #title>
      <div class="caption">Установить начальные значения</div>
    </template>

    <template #close="{ closeDialog }">
      <div v-if="$vuetify.display.mdAndUp" class="d-flex">
        <e-btn variant="secondary" @click="closeDialog">Отмена</e-btn>
        <e-btn variant="primary" class="ml-4" :loading="isEditQuotaInitialValuesUpdateLoading" @click="handleSave">
          Сохранить
        </e-btn>
      </div>
    </template>

    <template #buttons>
      <div v-if="!$vuetify.display.mdAndUp" class="d-flex justify-end w-100 mt-3">
        <e-btn variant="secondary" @click="dialogRef.close()">Отмена</e-btn>
        <e-btn variant="primary" class="ml-4" :loading="isEditQuotaInitialValuesUpdateLoading" @click="handleSave">
          Сохранить
        </e-btn>
      </div>
    </template>

    <div class="paragraph-m_h text-secondary mb-6">
      Установите квоту для каждой категории номера или оставьте её под запрос
    </div>

    <e-data-items :items="tableItems" :headers="tableHeaders">
      <template v-slot:[`item.name`]="{ item }">
        <div>
          {{ item.name[0].value }}
        </div>
      </template>
      <template v-slot:[`item.dataItemSecondRow`]="{ item }">
        <div v-if="isFormDirty && !+item.quota && !item.request" class="d-flex align-center">
          <e-icon name="alert" size="16" class="text-red mr-2" />
          <div class="text-red paragraph-xs font-400">Укажите квоту категории номера или выставьте её под запрос</div>
        </div>
      </template>
      <template v-slot:[`item.quota`]="{ item }">
        <e-input v-model="item.quota" :maska="quotaMask" @update:model-value="handleQuotaUpdate(item)" />
      </template>
      <template v-slot:[`item.request`]="{ item }">
        <e-checkbox v-model="item.request" @update:model-value="handleRequestUpdate(item)" />
      </template>
    </e-data-items>
  </e-dialog>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { QuotaInitialValuesRoomCategoryType } from '@/parts/tariffs/store/quota';
import EDataItems from '@/core/components/common/data-items/EDataItems.vue';
import EDialog from '@/core/components/modals/Dialog.vue';
import { EBtn, ECheckbox, EIcon, EInput } from '@/core/components/ui';
import { quotaMask } from '@/core/consts/masks';
import { useQuotaStore } from '@/parts/tariffs/store/quota';

type TableItemType = QuotaInitialValuesRoomCategoryType & { quota: string; request: boolean };

const props = defineProps<{
  roomCategories: QuotaInitialValuesRoomCategoryType[];
  tariffId: string;
}>();

const {
  setActiveQuota,
  editQuotaInitialValuesCancel,
  editQuotaInitialValuesUpdate,
  isEditQuotaInitialValuesUpdateLoading,
} = useQuotaStore();

const dialogRef = ref();
const isFormDirty = ref(false);

const tableItems = ref<TableItemType[]>([]);

const tableHeaders = [
  {
    key: 'name',
    title: 'Категория номера',
  },
  {
    key: 'type.name',
    title: 'Тип',
    sortable: false,
  },
  {
    key: 'seat.baseQuantity',
    title: 'Осн.',
    sortable: false,
    width: 120,
  },
  {
    key: 'seat.extQuantity',
    title: 'Доп.',
    sortable: false,
    width: 120,
  },
  {
    key: 'quota',
    title: 'Квота ',
    sortable: false,
    width: 160,
  },
  {
    key: 'request',
    title: 'Под запрос ',
    sortable: false,
    width: 108,
  },
];

const handleDialogOpen = () => {
  tableItems.value = props.roomCategories.map((item) => ({
    ...item,
    quota: '',
    request: false,
  }));
};

const handleDialogClose = () => {
  editQuotaInitialValuesCancel();
};

const handleQuotaUpdate = (item: TableItemType) => {
  item.request = false;
};

const handleRequestUpdate = (item: TableItemType) => {
  item.quota = '';
};

const handleSave = async () => {
  isFormDirty.value = true;

  if (tableItems.value.some((item) => !+item.quota && !item.request)) {
    return;
  }

  const result = await editQuotaInitialValuesUpdate({
    tariffScheduleCid: props.tariffId,
    roomCategories: tableItems.value.map((item) => ({
      roomCategoryCID: item.cid,
      roomCount: item.request ? 0 : +item.quota,
    })),
  });

  if (!result.errors && result.data) {
    setActiveQuota(result.data.updateQuotaAddRooms.quotaView);
    dialogRef.value.close();
  }
};
</script>
